import { createStore, createStateHook, createActionsHook } from 'react-sweet-state';
import { initialState, actions } from '@codexporer.io/expo-link-stores';
import filter from 'lodash/filter';
import find from 'lodash/find';
import values from 'lodash/values';
import reduce from 'lodash/reduce';
import { graphql } from './graphql';
import { awsConfig } from '../aws.config';

export const RemoteConfigProperty = {
    /**
     * Version before and including on which force update screen should be shown at the app start on android
     */
    ANDROID_FORCE_UPDATE_VERSION: 'android_force_update_version',
    /**
     * Version before and including on which force update screen should be shown at the app start on ios
     */
    IOS_FORCE_UPDATE_VERSION: 'ios_force_update_version',
    /**
     * Card data for journal generic card
     */
    GENERIC_JOURNAL_CARD_DATA: 'generic_journal_card_data',
    /**
     * Credits required for 6 months premium reward
     */
    CREDITS_PREMIUM_6_M: 'credits_premium_6_m'
};

const getString = value => value;

const getJSON = value => {
    try {
        return JSON.parse(getString(value));
    } catch (error) {
        // eslint-disable-next-line no-console
        console.error(error);
        return null;
    }
};

const getNumber = value => {
    if (!value) {
        return null;
    }

    return +value;
};

/* const getBoolean = value => (
    value === 'true' ?
        true :
        value === 'false' ?
            false :
            undefined
); */

const remoteConfigPropertyConfigurationsMap = {
    [RemoteConfigProperty.ANDROID_FORCE_UPDATE_VERSION]: {
        defaultValue: '0',
        getValue: getString
    },
    [RemoteConfigProperty.IOS_FORCE_UPDATE_VERSION]: {
        defaultValue: '0',
        getValue: getString
    },
    [RemoteConfigProperty.GENERIC_JOURNAL_CARD_DATA]: {
        defaultValue: null,
        getValue: getJSON
    },
    [RemoteConfigProperty.CREDITS_PREMIUM_6_M]: {
        defaultValue: 0,
        getValue: getNumber
    }
};

const DEFAULT_VALUES = reduce(
    values(RemoteConfigProperty),
    (defaultValues, key) => {
        defaultValues[key] = remoteConfigPropertyConfigurationsMap[key].defaultValue;
        return defaultValues;
    },
    {}
);

const transformConfigValues = config => reduce(
    values(RemoteConfigProperty),
    (values, key) => {
        const {
            getValue,
            defaultValue
        } = remoteConfigPropertyConfigurationsMap[key];
        values[key] = getValue(find(config, { name: key })?.value) ?? defaultValue;
        return values;
    },
    {}
);

const localInitialState = {
    config: DEFAULT_VALUES,
    isLoading: null,
    error: null
};

const appRemoteConfigFieldsQueryFragment = `
    fragment AppRemoteConfigFields on AppRemoteConfig {
        id
        name
        value
        _lastChangedAt
        _version
        _deleted
    }
`;

const listAppRemoteConfigQuery = `
    ${appRemoteConfigFieldsQueryFragment}
    query ListAppRemoteConfigs(
        $limit: Int
        $nextToken: String
    ) {
        result: listAppRemoteConfigs(
            limit: $limit
            nextToken: $nextToken
        ) {
            items {
                ...AppRemoteConfigFields
            }
            nextToken
        }
    }
`;

const setConfig = config => ({ setState }) => {
    setState({ config });
};

const setIsLoading = isLoading => ({ setState }) => {
    setState({ isLoading });
};

const setError = error => ({ setState }) => {
    setState({ error });
};

const fetchConfig = () => async ({ getState, dispatch }) => {
    const { isLoading } = getState();
    if (isLoading) {
        return;
    }

    try {
        dispatch(setError(null));
        dispatch(setIsLoading(true));

        const config = [];
        const fetchData = async ({ nextToken }) => {
            const result = await graphql({
                query: listAppRemoteConfigQuery,
                variables: {
                    limit: 100,
                    nextToken
                },
                authToken: awsConfig.awsmobile.aws_appsync_apiKey,
                authMode: 'apiKey'
            });

            config.push(
                ...filter(
                    result.data.result?.items,
                    ({ _deleted }) => _deleted !== true
                )
            );

            nextToken = result.data.result?.nextToken;
            if (nextToken) {
                await fetchData({ nextToken });
            }
        };

        await fetchData({ nextToken: null });

        dispatch(setIsLoading(false));
        dispatch(setConfig(transformConfigValues(config)));
    } catch (error) {
        dispatch(setIsLoading(false));
        dispatch(setError(error));
        dispatch(setConfig({ ...DEFAULT_VALUES }));
    }
};

const Store = createStore({
    initialState: {
        ...initialState,
        ...localInitialState
    },
    actions: {
        ...actions,
        fetchConfig
    },
    name: 'AppRemoteConfig'
});

const useAppRemoteConfigState = createStateHook(
    Store,
    {
        selector: (
            {
                config,
                isLoading,
                error
            }
        ) => ({
            config,
            isLoading: isLoading || isLoading === null,
            error
        })
    }
);

export const useAppRemoteConfigActions = createActionsHook(Store);

export const useAppRemoteConfig = () => {
    const { config } = useAppRemoteConfigState();
    return config;
};

export const useIsAppRemoteConfigInitialized = () => {
    const { isLoading } = useAppRemoteConfigState();
    return !isLoading;
};

export const useFetchAppRemoteConfig = () => {
    const { fetchConfig } = useAppRemoteConfigActions();
    return fetchConfig;
};
